import type { ValueEvent } from '@adornis/base/utilTypes.js';
import type { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { FormController } from '@adornis/forms/x-form-controller.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import '@chancenwerk/components/chancen-component-choose-intensive-courses.js';
import type { Course } from '@chancenwerk/db/courses/Course.js';
import { Mitgliedschaften } from '@chancenwerk/db/enums/enums.js';
import type { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Subscription } from 'rxjs';
import type { KPF } from '../../../../src/public-access/api/KPFPublicInformationAPI.js';
import ImageClientsSupport from './../assets/Classroom-med-min.png';
import { AnmeldungPageRegistrationBase } from './anmeldung-component-formular-base.js';

/**
 * @customElement anmeldung-component-formular-lkk
 */
@customElement('anmeldung-component-formular-lkk')
export class AnmeldungPageRegistrationLKK extends AnmeldungPageRegistrationBase {
  @state() private readonly _size = new WindowSizeController(this);

  @property({ attribute: false }) form!: FormController<Student>;
  @property({ attribute: false }) translation!: TranslationController<any>;
  @property({ attribute: false }) student!: RXController<Student>;
  @property({ attribute: false }) choosenKPF!: RXController<KPF | undefined>;
  @property({ attribute: false }) subjects!: RXController<string[]>;
  @property({ attribute: false }) intensiveCourses!: RXController<Course[]>;
  @property({ attribute: 'school-level' }) schoolLevel: 'us' | 'os' = 'us';

  _studentSub?: Subscription;

  override connectedCallback(): void {
    super.connectedCallback();

    // ! As we pass RXController instances at the moment components that have
    // ! student.value as property won't correctly get updated. So we manually
    // ! trigger a update for now. This should be removed by doing more refactoring
    // ! of the formular steps.
    this._studentSub = this.student.observable.subscribe(async s => {
      this.requestUpdate('student');
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();

    this._studentSub?.unsubscribe();
  }

  override render() {
    return html`
      <cr-flex class="section-container" space="xl">
        <cr-image disabled .value=${ImageClientsSupport} mode="contain"> </cr-image>

        <cr-flex class="sc-content">
          <cr-flex space="lg">
            <cr-text ${css({ textAlign: 'justify' })}>
              Hiermit melde ich mich zur CHANCENreise an und möchte gerne von Chancenwerk e.V. auf dem Weg zu meinem
              Schulabschluss begleitet werden. Im Verlauf meiner CHANCENreise erhalte ich Workshops und Zertifikate.
              Dabei steht mir als Ansprechperson ein Coach zur Verfügung.
            </cr-text>
            <cr-flex space="md" horizontal crossaxis-center>
              <x-radiogroup ${this.form.field('mitgliedschaft')} @value-picked=${() => this.requestUpdate()}>
                <cr-grid columns=${this._size.mobile ? '1fr' : '0.5fr 1fr'} space="md">
                  <x-radiobutton label="Option 1" selectable=${Mitgliedschaften.HILFE_GEBEN}></x-radiobutton>
                  <cr-text ${css({ textAlign: 'justify' })}>
                    Ich nehme an der CHANCENreise teil und helfe meinen jüngeren Mitschüler:innen in der Lernförderung.
                    Ich habe <b>kein Interesse</b> am Intensivkursangebot.
                  </cr-text>
                  <x-radiobutton label="Option 2" selectable=${Mitgliedschaften.HILFE_GEBEN_NEHMEN}> </x-radiobutton>
                  <cr-text ${css({ textAlign: 'justify' })}>
                    Ich nehme an der CHANCENreise teil und helfe meinen jüngeren Mitschüler:innen in der Lernförderung.
                    Ich möchte <b>am kostenlosen Intensivkursangebot teilnehmen</b>.
                  </cr-text>
                </cr-grid>
              </x-radiogroup>
            </cr-flex>

            <anmeldung-component-formular-lkk-us
              .form=${this.form}
              .translation=${this.translation}
              .student=${this.student}
              .choosenKPF=${this.choosenKPF}
              .schoolLevel=${this.schoolLevel}
              @value-picked=${(e: ValueEvent<Student>) => this.student.next(e.detail.value)}
            >
            </anmeldung-component-formular-lkk-us>

            <cr-flex horizontal end crossaxis-center space="sm">
              <cr-button
                @click=${async () => {
                  this.form.validate().catch(console.error);
                }}
              >
                ${this.translation.translate('next')}
              </cr-button>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }
}
